import { ReactElement, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config/env';
import { useNavigate } from 'react-router-dom';
import { grievancesAPI, GrievanceStatus } from '../../services/api';
import { CheckCircle, Clock, AlertTriangle, Filter, RefreshCw, Paperclip, Download, Printer } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { GrievanceDetailButton } from '../../components/GrievanceDetailModal';

interface Ministry {
  id: number;
  name: string;
  isActive: boolean;
}

interface Attachment {
  name: string;
  url?: string;
}

interface Grievance {
  id: number;
  grievanceId: string;
  subject?: string;
  title?: string;
  description?: string;
  status: GrievanceStatus;
  ministry_name?: string;
  fullName?: string;
  submittedBy?: string;
  createdAt: string;
  attachments?: (string | Attachment)[] | null;
}

/** Normalise attachments regardless of whether they are stored as strings or objects */
const normalizeAttachments = (raw?: (string | Attachment)[] | null): Attachment[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) =>
    typeof item === 'string' ? { name: item } : { name: item.name, url: item.url }
  );
};

/** Open a dedicated print window for a single supporting document */
const printDocument = (file: Attachment) => {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  const absoluteUrl = file.url
    ? file.url.startsWith('http')
      ? file.url
      : `${window.location.origin}${file.url.startsWith('/') ? '' : '/'}${file.url}`
    : null;

  const isImage = absoluteUrl && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(absoluteUrl);
  const isPdf   = absoluteUrl && /\.pdf$/i.test(absoluteUrl);

  let body = '';
  if (isImage) {
    body = `<img src="${absoluteUrl}" style="max-width:100%;height:auto;" onload="window.print()" />`;
  } else if (isPdf) {
    body = `<iframe src="${absoluteUrl}" style="width:100%;height:95vh;border:none;" onload="window.print()"></iframe>`;
  } else if (absoluteUrl) {
    body = `<p>File: <a href="${absoluteUrl}">${file.name}</a></p><script>window.print();<\/script>`;
  } else {
    body = `<p>${file.name} — no URL available</p>`;
  }

  win.document.write(`<!DOCTYPE html><html><head><title>${file.name}</title>
    <style>body{margin:0;font-family:Arial,sans-serif;} @media print{a{display:none}}</style>
  </head><body>${body}</body></html>`);
  win.document.close();
};

const AdminGrievances = (): ReactElement => {
  const [items, setItems] = useState<Grievance[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [forwardOpenId, setForwardOpenId] = useState<string | null>(null);
  const [selectedMinistry, setSelectedMinistry] = useState<number | ''>('');

  const navigate = useNavigate();

  const fetchGrievances = async () => {
    setLoading(true);
    try {
      const res = await grievancesAPI.getAll();
      setItems(res.data || []);
    } catch (error) {
      toast.error('Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrievances();
  }, []);

  const loadMinistries = async () => {
    try {
      const resp = await fetch(`${API_URL}/ministries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data: Ministry[] = await resp.json();
      setMinistries(data);
    } catch (err) {
      console.error('Failed to load ministries', err);
      toast.error('Failed to load ministries');
    }
  };

  useEffect(() => {
    loadMinistries();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return items;
    return items.filter(g => g.status === statusFilter);
  }, [items, statusFilter]);

  const statusOptions: {
    value: GrievanceStatus;
    label: string;
    icon: any;
    color: string;
  }[] = [
    {
      value: "pending",
      label: "Pending",
      icon: Clock,
      color: "text-yellow-700 bg-yellow-100",
    },
    {
      value: "in-progress",
      label: "In Progress",
      icon: RefreshCw,
      color: "text-blue-700 bg-blue-100",
    },
    {
      value: "resolved",
      label: "Resolved",
      icon: CheckCircle,
      color: "text-green-700 bg-green-100",
    },
    {
      value: "rejected",
      label: "Rejected",
      icon: AlertTriangle,
      color: "text-red-700 bg-red-100",
    },
  ];

  const handleUpdateStatus = async (row: Grievance, status: GrievanceStatus) => {
    try {
      await grievancesAPI.updateStatus(row.id, { status });
      toast.success(`Status updated to ${status}`);
      setItems(prev => prev.map(it => it.id === row.id ? { ...it, status } : it));
    } catch {
      toast.error('Failed to update status');
    }
  };

  const confirmForward = async (row: Grievance) => {
    if (!selectedMinistry) {
      toast.error('Select a ministry');
      return;
    }
    
    // Find the ministry name from the selected ministry ID
    const selectedMinistryData = ministries.find(m => m.id === selectedMinistry);
    const ministryName = selectedMinistryData?.name;
    
    if (!ministryName) {
      toast.error('Ministry not found');
      return;
    }
    
    try {
      await grievancesAPI.addTimeline(row.grievanceId, { status: 'approved', message: `Forwarded to ${ministryName}` });
      await fetch(`${API_URL}/grievances/${encodeURIComponent(row.grievanceId)}/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ ministry_name: ministryName })
      });
      toast.success('Grievance forwarded');
      setForwardOpenId(null);
      setSelectedMinistry('');
      fetchGrievances();
    } catch {
      toast.error('Failed to forward');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Grievance Management</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => { setRefreshing(true); fetchGrievances().finally(() => setRefreshing(false)); }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Grievances List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b px-4 py-3 text-sm text-gray-700">
          {loading ? 'Loading grievances…' : `${filtered.length} grievances`}
        </div>
        <div className="divide-y">
          {filtered.map((g) => {
            const current = statusOptions.find(s => s.value === g.status);
            const files = normalizeAttachments(g.attachments);
            return (
              <div key={g.id} className="p-4">
                {/* Top row: info + actions */}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-gray-600">{g.grievanceId}</span>
                      <span className="font-semibold text-gray-900 truncate">{g.subject || g.title}</span>
                    </div>
                    <div className="text-sm text-gray-600 truncate max-w-2xl">{g.description}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {g.ministry_name ? (
                        <span>Forwarded to: <span className="font-semibold text-indigo-600">{g.ministry_name}</span></span>
                      ) : (
                        <span>Not forwarded</span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-800">
                      Grievance ID:{' '}
                      <button
                        type="button"
                        onClick={() => navigate(`/super-admin/reviews?id=${encodeURIComponent(g.grievanceId)}`)}
                        className="font-mono font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                      >
                        {g.grievanceId}
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">Submitted by {g.fullName || g.submittedBy} • {new Date(g.createdAt).toLocaleString()}</div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {current && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${current.color}`}>
                        <current.icon className="h-3 w-3" /> {current.label}
                      </span>
                    )}

                    <select
                      className="border rounded-md px-2 py-1 text-sm"
                      value={g.status}
                      onChange={(e) => handleUpdateStatus(g, e.target.value as GrievanceStatus)}
                    >
                      {statusOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>

                    <GrievanceDetailButton grievanceId={g.grievanceId} />

                    <div className="relative">
                      <button
                        onClick={() => setForwardOpenId(prev => prev === g.grievanceId ? null : g.grievanceId)}
                        className="inline-flex items-center gap-2 px-2 py-1 text-sm rounded-md border hover:bg-gray-50"
                      >
                        Forward
                      </button>
                      {forwardOpenId === g.grievanceId && (
                        <div className="absolute right-0 mt-2 bg-white border rounded shadow p-3 z-10 w-64">
                          <div className="text-sm mb-2">Select Ministry</div>
                          <select
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={selectedMinistry}
                            onChange={(e) => setSelectedMinistry(Number(e.target.value))}
                          >
                            <option value="">-- Select --</option>
                            {ministries
                              .filter(m => m.isActive)
                              .map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                          </select>
                          <div className="flex justify-end gap-2 mt-3">
                            <button className="px-2 py-1 text-sm" onClick={() => { setForwardOpenId(null); setSelectedMinistry(''); }}>Cancel</button>
                            <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded" onClick={() => confirmForward(g)}>Send</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Supporting Documents Section */}
                {files.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <Paperclip className="h-3.5 w-3.5" />
                      Supporting Documents ({files.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {files.map((file, i) => {
                        const absoluteUrl = file.url
                          ? file.url.startsWith('http')
                            ? file.url
                            : `${window.location.origin}${file.url.startsWith('/') ? '' : '/'}${file.url}`
                          : null;
                        return (
                          <div
                            key={i}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800"
                          >
                            <Paperclip className="h-3 w-3 text-blue-500 flex-shrink-0" />
                            <span className="max-w-[180px] truncate font-medium" title={file.name}>{file.name}</span>
                            {absoluteUrl && (
                              <>
                                <a
                                  href={absoluteUrl}
                                  download={file.name}
                                  className="ml-1 p-0.5 rounded hover:bg-blue-100 text-blue-600 hover:text-blue-800 transition-colors"
                                  title={`Download ${file.name}`}
                                >
                                  <Download className="h-3 w-3" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => printDocument(file)}
                                  className="p-0.5 rounded hover:bg-blue-100 text-blue-600 hover:text-blue-800 transition-colors"
                                  title={`Print ${file.name}`}
                                >
                                  <Printer className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">No grievances found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGrievances;